/**
 * Brief パネルの「参考資料」欄。
 * PDF / Word(.docx) / Excel / CSV をドラッグ＆ドロップ（またはクリック選択）で受け取り、
 * ブラウザ内で文字を取り出して、生成・全体再生成のときに AI へ渡す。
 * ファイルと取り出した文字はどこにも保存しない（ページを離れると消える）。
 * 状態は親 (BriefPanel) が持つ。
 */
import {
  LANDING_REFERENCE_MAX_FILES,
  LANDING_REFERENCE_MAX_FILE_MB,
  LANDING_REFERENCE_MAX_TOTAL_CHARS,
  type LandingReferenceDocument,
  type LandingReferenceKind,
} from '@goenhub/shared';
import { FileSpreadsheet, FileText, FileType, FileUp, Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { REFERENCE_ACCEPT, detectReferenceKind, extractReference } from '../utils/referenceExtract';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';

export interface ReferenceFileItem {
  id: string;
  name: string;
  kind: LandingReferenceKind;
  status: 'reading' | 'ready' | 'error';
  enabled: boolean;
  text: string;
  truncated: boolean;
  summary: string;
  error?: string;
}

/** ON かつ読み取り済みの資料を、AI に渡す形にする（合計文字数の上限内に収める）。 */
export function toReferenceDocuments(items: ReferenceFileItem[]): LandingReferenceDocument[] {
  const out: LandingReferenceDocument[] = [];
  let remaining = LANDING_REFERENCE_MAX_TOTAL_CHARS;
  for (const it of items) {
    if (!it.enabled || it.status !== 'ready' || !it.text || remaining <= 0) continue;
    const cut = it.text.length > remaining;
    out.push({
      name: it.name,
      kind: it.kind,
      text: cut ? it.text.slice(0, remaining) : it.text,
      truncated: cut || it.truncated,
    });
    remaining -= Math.min(it.text.length, remaining);
  }
  return out;
}

interface Props {
  items: ReferenceFileItem[];
  onChange: (updater: (prev: ReferenceFileItem[]) => ReferenceFileItem[]) => void;
  disabled?: boolean;
}

export function ReferenceFilesSection({ items, onChange, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const preview = items.find((i) => i.id === previewId) ?? null;

  const onCount = items.filter((i) => i.enabled && i.status === 'ready').length;
  const sentChars = toReferenceDocuments(items).reduce((n, d) => n + d.text.length, 0);
  const enabledChars = items
    .filter((i) => i.enabled && i.status === 'ready')
    .reduce((n, i) => n + i.text.length, 0);

  const addFiles = (list: FileList | null | undefined) => {
    if (!list || disabled) return;
    let count = items.length;
    for (const file of Array.from(list)) {
      const kind = detectReferenceKind(file.name);
      if (!kind) {
        toast.error(`${file.name}: PDF・Word(.docx)・Excel・CSV のみ追加できます`);
        continue;
      }
      if (file.size > LANDING_REFERENCE_MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${file.name}: ${LANDING_REFERENCE_MAX_FILE_MB}MB 以下にしてください`);
        continue;
      }
      if (count >= LANDING_REFERENCE_MAX_FILES) {
        toast.error(`参考資料は ${LANDING_REFERENCE_MAX_FILES} 件までです`);
        break;
      }
      count++;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      onChange((prev) => [
        ...prev,
        {
          id,
          name: file.name,
          kind,
          status: 'reading',
          enabled: true,
          text: '',
          truncated: false,
          summary: '',
        },
      ]);
      extractReference(file, kind)
        .then((r) => {
          onChange((prev) =>
            prev.map((it) =>
              it.id !== id
                ? it
                : r.text
                  ? {
                      ...it,
                      status: 'ready',
                      text: r.text,
                      truncated: r.truncated,
                      summary: r.summary,
                    }
                  : {
                      ...it,
                      status: 'error',
                      enabled: false,
                      error:
                        kind === 'pdf'
                          ? '文字を読み取れませんでした（画像だけの PDF の可能性があります）'
                          : '文字を読み取れませんでした（中身が空です）',
                    },
            ),
          );
        })
        .catch((e) => {
          onChange((prev) =>
            prev.map((it) =>
              it.id === id
                ? {
                    ...it,
                    status: 'error',
                    enabled: false,
                    error: `読み取りに失敗しました: ${e instanceof Error ? e.message : String(e)}`,
                  }
                : it,
            ),
          );
        });
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2 pt-3 border-t border-[var(--border)]">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-semibold text-[var(--text-muted)] tracking-wide">
          参考資料
        </div>
        <div className="text-[10px] text-[var(--text-muted)]">
          <span className="text-[var(--accent)] font-semibold">{onCount}</span> / {items.length} 件
        </div>
      </div>
      <p className="text-[10px] text-[var(--text-muted)] -mt-1 leading-relaxed">
        スイッチが ON の資料の中身を AI
        が読んで生成に使います。資料は保存されず、このページを離れると消えます。
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={REFERENCE_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />
      <div
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`rounded-md border border-dashed p-3 text-center transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${
          dragOver
            ? 'border-[var(--accent)] bg-[var(--bg-hover)]'
            : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
        }`}
      >
        <FileUp className="size-4 mx-auto mb-1 text-[var(--text-muted)]" />
        <div className="text-xs text-[var(--text-secondary)]">
          ここにファイルをドロップ／クリックして選ぶ
        </div>
        <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
          PDF・Word(.docx)・Excel・CSV／{LANDING_REFERENCE_MAX_FILES}件・1件
          {LANDING_REFERENCE_MAX_FILE_MB}MBまで
        </div>
      </div>

      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((it) => (
            <div
              key={it.id}
              className={`rounded border border-[var(--border)] px-2 py-1.5 ${
                it.enabled && it.status === 'ready' ? '' : 'bg-[var(--bg-elevated)]'
              }`}
            >
              <div className="flex items-center gap-2">
                {it.status === 'reading' ? (
                  <Loader2 className="size-3.5 animate-spin text-[var(--text-muted)] flex-shrink-0" />
                ) : (
                  <Switch
                    id={`reference-${it.id}`}
                    checked={it.enabled}
                    disabled={it.status !== 'ready' || disabled}
                    onChange={(e) => {
                      const v = e.target.checked;
                      onChange((prev) =>
                        prev.map((x) => (x.id === it.id ? { ...x, enabled: v } : x)),
                      );
                    }}
                    aria-label={`${it.name} を AI に渡す`}
                  />
                )}
                <KindIcon kind={it.kind} />
                <span
                  className="flex-1 min-w-0 truncate text-xs text-[var(--text-primary)]"
                  title={it.name}
                >
                  {it.name}
                </span>
                {it.status === 'ready' && (
                  <button
                    type="button"
                    onClick={() => setPreviewId(it.id)}
                    className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] flex-shrink-0"
                  >
                    確認
                  </button>
                )}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange((prev) => prev.filter((x) => x.id !== it.id))}
                    aria-label={`${it.name} を外す`}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)] flex-shrink-0"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
              <div
                className={`text-[10px] mt-0.5 pl-1 ${
                  it.status === 'error'
                    ? 'text-[var(--danger,#dc2626)]'
                    : 'text-[var(--text-muted)]'
                }`}
              >
                {it.status === 'reading' && '読み取り中…'}
                {it.status === 'ready' &&
                  `${it.summary}${it.truncated ? '（長いため途中まで渡します）' : ''}`}
                {it.status === 'error' && it.error}
              </div>
            </div>
          ))}
        </div>
      )}

      {enabledChars > sentChars && (
        <p className="text-[10px] text-[var(--text-muted)]">
          合計が {LANDING_REFERENCE_MAX_TOTAL_CHARS.toLocaleString()} 字を超えるため、上から順に
          {LANDING_REFERENCE_MAX_TOTAL_CHARS.toLocaleString()} 字までを AI に渡します。
        </p>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm truncate pr-6">{preview?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-[var(--text-muted)]">
            AI が読む中身です（{preview?.summary}）。
            {preview?.truncated && '長いため、ここに表示されている所までを渡します。'}
          </p>
          <pre className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed bg-[var(--bg-elevated)] border border-[var(--border)] rounded p-3 max-h-[60vh] overflow-y-auto">
            {preview?.text}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KindIcon({ kind }: { kind: LandingReferenceKind }) {
  const cls = 'size-3.5 text-[var(--text-secondary)] flex-shrink-0';
  if (kind === 'xlsx' || kind === 'csv') return <FileSpreadsheet className={cls} />;
  if (kind === 'docx') return <FileType className={cls} />;
  return <FileText className={cls} />;
}
